import React, { useState, useEffect } from 'react';
import { Table, Select, Button, message, Modal } from 'antd';
import axiosInstance from 'config/axios';
import endpoints from 'v2/config/endpoints';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import EditDrawer from '../VlcReportConfig/editConfig';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

const ReportCardTable = () => {
  const selectedAcademicYear = useSelector(
    (state) => state.commonFilterReducer?.selectedYear
  );
  const history = useHistory();
  const [data, setData] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [gradesList, setGradesList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [grade, setGrade] = useState(null);
  const [termId, setTermId] = useState('1');
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [editingType, setEditingType] = useState('');
  const [existingData, setExistingData] = useState([]);

  useEffect(() => {
    fetchBranches({ session_year: selectedAcademicYear?.id });
  }, []);

  useEffect(() => {
    if (selectedBranch && grade && termId) {
      fetchData();
    }
  }, [selectedBranch, grade, termId]);

  const fetchBranches = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${endpoints.academics.branches}`, {
        params: params,
      });
      setBranchList(response.data?.data?.results);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
    setLoading(false);
  };

  const fetchGrades = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${endpoints.academics.grades}`, {
        params: params,
      });
      setGradesList(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
    setLoading(false);
  };

  const handleBranchChange = (value, key) => {
    console.log({ value, key });
    setSelectedBranch(value);
    setGrade(null);
    fetchGrades({
      session_year: selectedAcademicYear?.id,
      branch_id: key?.key,
    });
  };

  const handleEdit = (record, type) => {
    setEditingData(record);
    setEditingType(type);
    let existingData;
    switch (type) {
      case 'subject':
        existingData = data;
        break;
      case 'level':
        existingData =
          data.find((item) => item.id === record.parent)?.level_details || [];
        break;
      case 'param':
        existingData =
          data.find((item) => item.id === record.subject)?.subject_params || [];
        break;
      case 'grade':
        existingData =
          data.find((item) => item.id === record.subject)?.grade_details || [];
        break;
      default:
        existingData = [];
    }
    setExistingData(existingData);
    setDrawerVisible(true);
  };

  const handleDelete = async (id, type) => {
    try {
      const params = {};
      params[`${type}_update`] = id;

      const response = await axiosInstance.delete(
        `${endpoints.assessmentReportConfig.getVlcTemplate}`,
        {
          params,
        }
      );
      if (response.status === 200) {
        message.success('Deleted successfully');
        fetchData();
      }
    } catch (error) {
      message.error('Failed to delete the item');
      console.error('Error deleting item:', error);
    }
  };

  const showDeleteConfirm = (id, type) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this item?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleDelete(id, type);
      },
    });
  };

  const handleSubmit = async (values, type) => {
    try {
      let updateData;
      switch (type) {
        case 'subject':
          updateData = { subject_update: [values] };
          break;
        case 'level':
          updateData = { level_update: [values] };
          break;
        case 'param':
          updateData = { params_update: [values] };
          break;
        case 'grade':
          updateData = { grading_update: [values] };
          break;
        default:
          throw new Error('Invalid update type');
      }

      const response = await axiosInstance.put(
        `${endpoints.assessmentReportConfig.getVlcTemplate}?acad_session=${selectedBranch}&grade=${grade}&term_id=${termId}`,
        updateData
      );
      if (response.status === 200) {
        setDrawerVisible(false);
        message.success('Updated successfully');
        fetchData();
      }
    } catch (error) {
      message.error('Failed to update the data');
      console.error('Error updating item:', error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await axiosInstance.get(
        `${endpoints.assessmentReportConfig.getVlcTemplate}?acad_session=${selectedBranch}&grade=${grade}&term_id=${termId}`
      );
      setData(response.data.result);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const columns = [
    {
      title: <span className='th-white th-fw-700 '>Subject Name</span>,
      dataIndex: 'subject_name',
      key: 'subject_name',
    },
    {
      title: <span className='th-white th-fw-700 '>Term Name</span>,
      dataIndex: 'term_name',
      key: 'term_name',
    },
    {
      title: <span className='th-white th-fw-700 '>Sequence</span>,
      dataIndex: 'sequence',
      key: 'sequence',
    },
    {
      title: <span className='th-white th-fw-700 '>Action</span>,
      key: 'action',
      render: (_, record) => (
        <>
          <EditOutlined
            style={{
              fontSize: 20,
              cursor: 'pointer',
              color: '#1B4CCB',
              marginRight: 10,
            }}
            onClick={() => handleEdit(record, 'subject')}
          />
          <DeleteOutlined
            style={{
              fontSize: 20,
              cursor: 'pointer',
              color: '#ff4d4f',
            }}
            onClick={() => showDeleteConfirm(record.id, 'subject')}
          />
        </>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const childColumns = [
      {
        title: <span className='th-white th-fw-700 '>Participation Name</span>,
        dataIndex: 'subject_name',
        key: 'subject_name',
      },
      {
        title: <span className='th-white th-fw-700 '>Sequence</span>,
        dataIndex: 'sequence',
        key: 'sequence',
      },
      {
        title: <span className='th-white th-fw-700 '>Action</span>,
        key: 'action',
        render: (_, childRecord) => (
          <>
            <EditOutlined
              style={{
                fontSize: 20,
                cursor: 'pointer',
                color: '#1B4CCB',
                marginRight: 10,
              }}
              onClick={() => handleEdit(childRecord, 'level')}
            />
            <DeleteOutlined
              style={{ fontSize: 20, cursor: 'pointer', color: '#ff4d4f' }}
              onClick={() => showDeleteConfirm(childRecord.id, 'level')}
            />
          </>
        ),
      },
    ];

    const paramColumns = [
      {
        title: <span className='th-white th-fw-700 '>Parameter</span>,
        dataIndex: 'param_name',
        key: 'param_name',
      },
      {
        title: <span className='th-white th-fw-700 '>Sequence</span>,
        dataIndex: 'sequence',
        key: 'sequence',
      },
      {
        title: <span className='th-white th-fw-700 '>Action</span>,
        key: 'action',
        render: (_, paramRecord) => (
          <>
            <EditOutlined
              style={{
                fontSize: 20,
                cursor: 'pointer',
                color: '#1B4CCB',
                marginRight: 10,
              }}
              onClick={() => handleEdit(paramRecord, 'param')}
            />
            <DeleteOutlined
              style={{ fontSize: 20, cursor: 'pointer', color: '#ff4d4f' }}
              onClick={() => showDeleteConfirm(paramRecord.id, 'params')}
            />
          </>
        ),
      },
    ];

    const gradeColumns = [
      {
        title: <span className='th-white th-fw-700 '>Grade Scale</span>,
        dataIndex: 'grade_scale_name',
        key: 'grade_scale_name',
      },
      {
        title: <span className='th-white th-fw-700 '>Scale Name</span>,
        dataIndex: 'scale_name',
        key: 'scale_name',
      },
      {
        title: <span className='th-white th-fw-700 '>Action</span>,
        key: 'action',
        render: (_, gradeRecord) => (
          <>
            <EditOutlined
              style={{
                fontSize: 20,
                cursor: 'pointer',
                color: '#1B4CCB',
                marginRight: 10,
              }}
              onClick={() => handleEdit(gradeRecord, 'grade')}
            />
            <DeleteOutlined
              style={{ fontSize: 20, cursor: 'pointer', color: '#ff4d4f' }}
              onClick={() => showDeleteConfirm(gradeRecord.id, 'grading')}
            />
          </>
        ),
      },
    ];

    return (
      <div>
        <h4 className='th-16'>Participation</h4>
        <Table
          columns={childColumns}
          dataSource={record.level_details}
          pagination={false}
        />
        <h4 className='th-16'>Evaluation Parameters</h4>
        <Table
          columns={paramColumns}
          dataSource={record.subject_params}
          pagination={false}
        />
        <h4 className='th-16'>Grade Details</h4>
        <Table
          columns={gradeColumns}
          dataSource={record.grade_details}
          pagination={false}
        />
      </div>
    );
  };

  return (
    <div className='px-3'>
      <div className='row'>
        <div className='col-md-3'>
          <Select
            className='w-100'
            value={selectedBranch}
            onChange={handleBranchChange}
            style={{ marginRight: 16 }}
            placeholder='Select Branch'
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            optionFilterProp='children'
          >
            {branchList.map((branch) => (
              <Option key={branch.branch.id} value={branch.id}>
                {branch.branch?.branch_name}
              </Option>
            ))}
          </Select>
        </div>
        <div className='col-md-3'>
          <Select
            className='w-100'
            value={grade}
            onChange={(value) => setGrade(value)}
            style={{ marginRight: 16 }}
            placeholder='Select Grade'
            disabled={!selectedBranch}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            optionFilterProp='children'
          >
            {gradesList.map((grade) => (
              <Option key={grade.grade_id} value={grade.grade_id}>
                {grade.grade_name}
              </Option>
            ))}
          </Select>
        </div>
        <div className='col-md-3'>
          <Select
            className='w-100'
            value={termId}
            onChange={(value) => setTermId(value)}
            style={{ marginBottom: 16 }}
            placeholder='Select Term'
          >
            <Option value='1'>Semester 1</Option>
            <Option value='2'>Semester 2</Option>
            <Option value='3'>Semester 3</Option>
            <Option value='4'>Semester 4</Option>
          </Select>
        </div>
        <div className='col-md-3 text-right'>
          <Button
            type='primary'
            onClick={() => history.push('/report-config/create-vlc-template')}
          >
            Create Template
          </Button>
        </div>
      </div>
      <div className='col-md-12'>
        <Table
          columns={columns}
          expandable={{
            expandedRowRender: expandedRowRender,
            rowExpandable: (record) => record.level_details?.length > 0,
          }}
          dataSource={data}
          rowKey='id'
          loading={loading}
        />
      </div>
      <EditDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleSubmit}
        data={editingData}
        type={editingType}
        existingData={existingData || []}
      />
    </div>
  );
};

export default ReportCardTable;
